import React, { useState, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  Upload, X, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle,
  XCircle, SkipForward, Filter, Download,
} from 'lucide-react';
import { toast } from './ui/sonner';
import { DriverMatchCell, normalizeMatchStatus } from './DriverMatchCell';
import { LoadStatusPill } from './LoadStatusPill';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROW_BADGES = {
  ready:              { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2, label: 'Ready' },
  duplicate:          { color: 'bg-amber-100 text-amber-700',     icon: AlertTriangle, label: 'Duplicate' },
  error:              { color: 'bg-rose-100 text-rose-700',       icon: XCircle,       label: 'Error' },
  ignored_cancelled:  { color: 'bg-slate-100 text-slate-600',     icon: SkipForward,   label: 'Canceled' },
};

const FILTER_TABS = [
  { key: 'all',                label: 'All' },
  { key: 'ready',              label: 'Ready' },
  { key: 'duplicate',          label: 'Duplicates' },
  { key: 'error',              label: 'Errors' },
  { key: 'ignored_cancelled',  label: 'Canceled' },
  { key: 'needs_confirmation', label: 'Driver: needs confirmation', match: true },
  { key: 'unlinked',           label: 'Driver: unlinked', match: true },
];
const rowMatch = (r) => (r.driver_id ? 'matched' : normalizeMatchStatus(r.driver_match_status));

const RowBadge = ({ status }) => {
  const b = ROW_BADGES[status] || ROW_BADGES.error;
  const Icon = b.icon;
  return (
    <span data-testid={`row-badge-${status}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${b.color}`}>
      <Icon className="w-3 h-3" strokeWidth={2.2} />
      {b.label}
    </span>
  );
};

/**
 * BulkLoadImportModal
 * ───────────────────
 * Two-step modal: (1) Pick file → call /loads/bulk-import/preview
 *                 (2) Review parsed rows → call /loads/bulk-import/commit
 *
 * Props:
 *  - open     : boolean
 *  - onClose  : () => void
 *  - onDone   : (summary) => void   // called after successful commit
 *  - drivers  : [{user_id, name, email, status}]   // for ambiguous-driver resolution
 */
const BulkLoadImportModal = ({ open, onClose, onDone, drivers = [], onAmazonDetected }) => {
  const fileRef = useRef(null);
  const [phase, setPhase] = useState('upload');           // 'upload' | 'review' | 'committing'
  const [uploading, setUploading] = useState(false);
  const [previewRes, setPreviewRes] = useState(null);     // server response
  const [rows, setRows] = useState([]);                   // editable copy of preview rows
  const [tab, setTab] = useState('all');
  const [duplicateMode, setDuplicateMode] = useState('skip');   // 'skip' | 'update'

  const activeDrivers = useMemo(
    () => drivers.filter((d) => d.status === 'active'),
    [drivers],
  );

  const reset = () => {
    setPhase('upload');
    setPreviewRes(null);
    setRows([]);
    setTab('all');
    setUploading(false);
  };

  const handleClose = () => {
    if (uploading || phase === 'committing') return;
    reset();
    onClose?.();
  };

  // ─── STEP 1: upload + preview ─────────────────────────────────────
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/loads/bulk-import/preview`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.amazon_relay_detected) {
        toast.info(res.data.detail || 'Amazon Relay export detected — switching to the load-list importer.');
        reset();
        onAmazonDetected?.(file);
        return;
      }
      setPreviewRes(res.data);
      // Default action per row
      const enriched = (res.data.rows || []).map((r) => {
        let action = 'create';
        if (r.row_status === 'duplicate') action = 'skip';   // changed below if user toggles "update"
        if (r.row_status === 'error') action = 'skip';
        if (r.row_status === 'ignored_cancelled') action = 'skip';
        return { ...r, action };
      });
      setRows(enriched);
      setPhase('review');
      toast.success(`Parsed ${res.data.total_rows} row(s) from "${file.name}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse file');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // ─── Apply duplicate mode (bulk re-assign action on duplicate rows) ──
  const applyDuplicateMode = (mode) => {
    setDuplicateMode(mode);
    setRows((prev) =>
      prev.map((r) =>
        r.row_status === 'duplicate'
          ? { ...r, action: mode === 'update' ? 'update' : 'skip' }
          : r,
      ),
    );
  };

  const setRowAction = (rowNumber, action) => {
    setRows((prev) => prev.map((r) => (r.row_number === rowNumber ? { ...r, action } : r)));
  };

  const patchDriver = (r, driverId, matchType, driverName) => {
    const newErrors = (r.errors || []).filter((e) => !e.toLowerCase().includes('driver'));
    const wasError = r.row_status === 'error' && (r.errors || []).some((e) => e.toLowerCase().includes('driver'));
    return {
      ...r,
      driver_id: driverId || null,
      driver_match_type: driverId ? (matchType || 'manual') : null,
      driver_resolved_name: driverId ? (driverName || null) : null,
      errors: driverId ? newErrors : r.errors,
      row_status: wasError && driverId ? 'ready' : r.row_status,
      action: driverId && (r.row_status === 'error' || wasError) ? 'create' : r.action,
    };
  };
  const setRowDriver = (rowNumber, driverId, matchType, driverName) => {
    setRows((prev) => prev.map((r) => (r.row_number !== rowNumber ? r : patchDriver(r, driverId, matchType, driverName))));
  };
  // Apply a confirmed match to every row in THIS file with the same imported name (never touches saved loads).
  const applyDriverToSameName = (rawName, driverId, matchType, driverName) => {
    const key = (rawName || '').trim().toLowerCase();
    if (!key) return;
    setRows((prev) => prev.map((r) => ((r.driver_name_raw || '').trim().toLowerCase() === key ? patchDriver(r, driverId, matchType, driverName) : r)));
    toast.success(`Applied ${driverName || 'driver'} to all rows named “${rawName}” in this file`);
  };

  // ─── STEP 2: commit ──────────────────────────────────────────────
  const summary = useMemo(() => {
    const counts = { create: 0, update: 0, skip: 0 };
    rows.forEach((r) => { counts[r.action] = (counts[r.action] || 0) + 1; });
    return counts;
  }, [rows]);

  const handleCommit = async () => {
    const toSend = rows
      .filter((r) => r.action !== 'skip')
      .map((r) => ({
        row_number: r.row_number,
        load_ref: r.load_ref,
        load_rate: r.load_rate,
        broker: r.broker,
        driver_id: r.driver_id,
        driver_name_raw: r.driver_name_raw,
        driver_match_type: r.driver_id ? (r.driver_match_type || 'manual') : null,
        dispatcher_id: r.dispatcher_id,
        pickup_date: r.pickup_date,
        delivery_date: r.delivery_date,
        pickup_city: r.pickup_city,
        pickup_state: r.pickup_state,
        delivery_city: r.delivery_city,
        delivery_state: r.delivery_state,
        pickup_location: r.pickup_location,
        delivery_location: r.delivery_location,
        deadhead_miles: r.deadhead_miles,
        loaded_miles: r.loaded_miles,
        total_miles: r.total_miles,
        driver_pay_type: r.driver_pay_type,
        load_status: r.load_status,
        factoring_company: r.factoring_company,
        notes: r.notes,
        action: r.action,
        duplicate_load_id: r.duplicate_load_id,
      }));

    if (!toSend.length) {
      toast.error('Nothing to import — all rows are set to "skip"');
      return;
    }

    setPhase('committing');
    try {
      const res = await axios.post(
        `${API}/loads/bulk-import/commit`,
        { rows: toSend, source_file: previewRes?.source_file || null },
        { withCredentials: true },
      );
      const { created, updated, skipped, failed, errors } = res.data;
      toast.success(`Imported ${created} new · updated ${updated} · skipped ${skipped}${failed ? ` · ${failed} failed` : ''}`);
      if (failed && errors?.length) {
        // Show first failure
        const first = errors[0];
        toast.error(`Row ${first.row}: ${first.error}`);
      }
      onDone?.(res.data);
      reset();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
      setPhase('review');
    }
  };

  const filteredRows = useMemo(() => {
    if (tab === 'all') return rows;
    if (tab === 'needs_confirmation' || tab === 'unlinked') return rows.filter((r) => rowMatch(r) === tab);
    return rows.filter((r) => r.row_status === tab);
  }, [rows, tab]);

  const tabCounts = useMemo(() => {
    const c = { all: rows.length, needs_confirmation: 0, unlinked: 0, matched: 0 };
    rows.forEach((r) => { c[r.row_status] = (c[r.row_status] || 0) + 1; c[rowMatch(r)] += 1; });
    return c;
  }, [rows]);

  if (!open) return null;

  return (
    <div
      data-testid="bulk-import-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-7xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Bulk Import Loads
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Upload a CSV or Excel file. We auto-detect Leoman and Amazon Relay templates.
            </p>
          </div>
          <button
            data-testid="bulk-import-close-btn"
            onClick={handleClose}
            disabled={uploading || phase === 'committing'}
            className="text-slate-400 hover:text-slate-700 transition-colors disabled:opacity-40"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {/* ────────────── STEP 1: UPLOAD ────────────── */}
          {phase === 'upload' && (
            <div className="p-10 flex flex-col items-center justify-center text-center">
              <div className="bg-[#002FA7]/5 border-2 border-dashed border-[#002FA7]/40 rounded-lg p-12 w-full max-w-2xl">
                <FileSpreadsheet className="w-16 h-16 text-[#002FA7] mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="text-lg font-semibold text-slate-900 mb-1">Drop your file here</h3>
                <p className="text-sm text-slate-600 mb-6">
                  Supported: .csv, .xlsx · Max 10 MB · Up to ~1000 rows per file
                </p>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFile}
                  className="hidden"
                  data-testid="bulk-import-file-input"
                />
                <button
                  data-testid="bulk-import-pick-file-btn"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-5 py-2.5 rounded-md font-semibold hover:bg-[#00247D] transition-all duration-200 disabled:opacity-60"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? 'Parsing…' : 'Choose File'}
                </button>

                <div className="mt-5 text-xs text-slate-500">
                  Don't have a file yet?{' '}
                  <a
                    data-testid="bulk-import-download-sample"
                    href={`${API}/loads/bulk-import/sample`}
                    className="inline-flex items-center gap-1 font-semibold text-[#002FA7] hover:underline"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download sample template
                  </a>
                </div>
              </div>

              <div className="mt-6 text-xs text-slate-500 max-w-2xl space-y-1.5 text-left bg-slate-50 rounded-md p-4">
                <p className="font-semibold text-slate-700 mb-1">Recognized templates</p>
                <p>• <span className="font-semibold">Leoman "Load Database"</span> — Load ID#, Load Rate, Driver Assignment, Pick Up / Delivery City &amp; State, Miles, Load Status…</p>
                <p>• <span className="font-semibold">Amazon Relay "Trips" export</span> — Trip ID, Load ID, Facility Sequence, Driver Name, Estimated Cost, Stop 1 / Stop 2 dates…</p>
                <p>• <span className="font-semibold">Generic</span> — any file with sensibly-named columns is parsed best-effort.</p>
              </div>
            </div>
          )}

          {/* ────────────── STEP 2: REVIEW ────────────── */}
          {phase !== 'upload' && previewRes && (
            <div className="flex flex-col h-full">
              {/* Banner */}
              <div className="bg-slate-50 border-b border-slate-200 px-6 py-3 flex items-center justify-between flex-wrap gap-3">
                <div className="text-sm text-slate-700">
                  <span className="font-semibold">{previewRes.template}</span>
                  {previewRes.sheet_name && <span className="text-slate-500"> · sheet "{previewRes.sheet_name}"</span>}
                  <span className="text-slate-500"> · {previewRes.total_rows} row(s) parsed</span>
                </div>
                <div className="text-xs text-slate-600 flex items-center gap-3" data-testid="bulk-import-match-summary">
                  <span><b className="text-emerald-700">{tabCounts.matched}</b> matched</span>
                  <span><b className="text-amber-700">{tabCounts.needs_confirmation}</b> need confirmation</span>
                  <span><b className="text-slate-700">{tabCounts.unlinked}</b> unlinked</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-slate-600 font-semibold">Duplicates:</span>
                  <button
                    data-testid="dup-mode-skip"
                    onClick={() => applyDuplicateMode('skip')}
                    className={`px-2.5 py-1 rounded-md font-semibold transition-colors ${duplicateMode === 'skip' ? 'bg-[#002FA7] text-white' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-100'}`}
                  >
                    Skip
                  </button>
                  <button
                    data-testid="dup-mode-update"
                    onClick={() => applyDuplicateMode('update')}
                    className={`px-2.5 py-1 rounded-md font-semibold transition-colors ${duplicateMode === 'update' ? 'bg-[#002FA7] text-white' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-100'}`}
                  >
                    Update existing
                  </button>
                </div>
              </div>

              {/* Filter tabs */}
              <div className="px-6 py-2 border-b border-slate-200 flex items-center gap-2 text-sm flex-wrap">
                <Filter className="w-3.5 h-3.5 text-slate-400" />
                {FILTER_TABS.map((t) => (
                  <button
                    key={t.key}
                    data-testid={`tab-${t.key}`}
                    onClick={() => setTab(t.key)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${tab === t.key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                  >
                    {t.label} <span className="opacity-70">({tabCounts[t.key] || 0})</span>
                  </button>
                ))}
              </div>

              {/* Rows table */}
              <div className="flex-1 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs sticky top-0 z-10">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">#</th>
                      <th className="text-left px-3 py-2 font-semibold">Status</th>
                      <th className="text-left px-3 py-2 font-semibold">Load Ref</th>
                      <th className="text-left px-3 py-2 font-semibold">Driver (imported → registered)</th>
                      <th className="text-left px-3 py-2 font-semibold">Route</th>
                      <th className="text-right px-3 py-2 font-semibold">Rate</th>
                      <th className="text-right px-3 py-2 font-semibold">Miles</th>
                      <th className="text-left px-3 py-2 font-semibold">Pickup</th>
                      <th className="text-left px-3 py-2 font-semibold">Delivery</th>
                      <th className="text-left px-3 py-2 font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredRows.length === 0 && (
                      <tr>
                        <td colSpan={10} className="px-3 py-10 text-center text-slate-500">
                          No rows in this filter.
                        </td>
                      </tr>
                    )}
                    {filteredRows.map((r) => (
                      <tr key={r.row_number} className={`hover:bg-slate-50 ${r.action === 'skip' ? 'opacity-50' : ''}`}>
                        <td className="px-3 py-2 text-slate-500 align-top">{r.row_number}</td>
                        <td className="px-3 py-2 align-top"><RowBadge status={r.row_status} /></td>
                        <td className="px-3 py-2 align-top font-mono text-xs">{r.load_ref || <span className="text-slate-400">—</span>}</td>
                        <td className="px-3 py-2 align-top">
                          <DriverMatchCell
                            testIdPrefix={`row-driver-${r.row_number}`}
                            originalName={r.driver_name_raw}
                            matchStatus={r.driver_match_status}
                            matchType={r.driver_match_type}
                            driverId={r.driver_id || null}
                            driverName={r.driver_resolved_name || (r.driver_match_candidates || []).find((c) => (c.id || c.driver_id) === r.driver_id)?.name}
                            candidates={r.driver_match_candidates}
                            drivers={activeDrivers}
                            onChange={(id, type, name) => setRowDriver(r.row_number, id, type, name)}
                            sameNameCount={r.driver_name_raw ? rows.filter((o) => o.row_number !== r.row_number && (o.driver_name_raw || '').trim().toLowerCase() === r.driver_name_raw.trim().toLowerCase() && o.driver_id !== r.driver_id).length : 0}
                            onApplyToAll={(id, type, name) => applyDriverToSameName(r.driver_name_raw, id, type, name)}
                          />
                          {r.driver_match_status === 'inactive' && <p className="text-[10px] text-rose-700 mt-1">Matched driver is not Active — pick another or import as Unlinked.</p>}
                          {r.load_status && <div className="mt-1.5"><LoadStatusPill status={r.load_status} size="xs" testId={`row-load-status-${r.row_number}`} /></div>}
                        </td>
                        <td className="px-3 py-2 align-top text-xs text-slate-600">
                          {r.pickup_date && <span>{r.pickup_date}</span>}
                          {r.delivery_date && <span> → {r.delivery_date}</span>}
                        </td>
                        <td className="px-3 py-2 align-top text-right tabular-nums">{r.load_rate ? `$${Number(r.load_rate).toLocaleString()}` : '—'}</td>
                        <td className="px-3 py-2 align-top text-right tabular-nums">{r.total_miles || '—'}</td>
                        <td className="px-3 py-2 align-top text-xs">
                          {r.pickup_city ? `${r.pickup_city}, ${r.pickup_state || ''}` : (r.pickup_location || '—')}
                        </td>
                        <td className="px-3 py-2 align-top text-xs">
                          {r.delivery_city ? `${r.delivery_city}, ${r.delivery_state || ''}` : (r.delivery_location || '—')}
                        </td>
                        <td className="px-3 py-2 align-top">
                          <select
                            data-testid={`row-action-${r.row_number}`}
                            value={r.action || 'create'}
                            onChange={(e) => setRowAction(r.row_number, e.target.value)}
                            className="border border-slate-300 rounded px-1.5 py-0.5 text-xs"
                            disabled={r.row_status === 'error' && !r.driver_id}
                          >
                            <option value="create" disabled={r.row_status === 'duplicate'}>Create</option>
                            {r.row_status === 'duplicate' && r.duplicate_load_id && (
                              <option value="update">Update existing</option>
                            )}
                            <option value="skip">Skip</option>
                          </select>
                          {r.errors?.length > 0 && (
                            <div className="text-[10px] text-rose-600 mt-1 max-w-[160px]">{r.errors.join('; ')}</div>
                          )}
                          {r.warnings?.length > 0 && (
                            <div className="text-[10px] text-amber-700 mt-1 max-w-[160px]">{r.warnings.join('; ')}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="border-t border-slate-200 px-6 py-3 flex items-center justify-between bg-slate-50">
                <div className="text-xs text-slate-600">
                  Will <span className="font-semibold text-emerald-700">create {summary.create}</span>
                  {' · '}
                  <span className="font-semibold text-blue-700">update {summary.update}</span>
                  {' · '}
                  <span className="font-semibold text-slate-700">skip {summary.skip}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid="bulk-import-back-btn"
                    onClick={reset}
                    disabled={phase === 'committing'}
                    className="px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200 rounded-md transition-colors disabled:opacity-50"
                  >
                    Re-upload
                  </button>
                  <button
                    data-testid="bulk-import-commit-btn"
                    onClick={handleCommit}
                    disabled={phase === 'committing' || (summary.create + summary.update) === 0}
                    className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-5 py-2 text-sm rounded-md font-semibold hover:bg-[#00247D] transition-all disabled:opacity-50"
                  >
                    {phase === 'committing' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    {phase === 'committing'
                      ? 'Importing…'
                      : `Import ${summary.create + summary.update} Load${(summary.create + summary.update) !== 1 ? 's' : ''}`}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkLoadImportModal;
